import { useRef, useEffect } from 'react';
import { IActiveCountry, IActiveDimension } from 'interfaces';
import DateSeparator from './EventsFeedDateSeparator';
import { getEventCountries, isEventNew } from './eventUtils';
import EventItem from './EventItem';
import { IEventsData } from './EventsFeedInterfaces';

type Props = {
    index: number;
    data: { activeEventID: number };
    setSize: (index: number, height: number) => void;
    windowWidth: number;
    isEventLoaded: (index: number) => boolean;
    onEventClick: (eventID: number) => void;
    countriesById: { [countryID: number]: IActiveCountry };
    events: IEventsData[];
    isLoading: boolean;
    includeMentionCountries: boolean;
    onEventDelete: (id: number) => void;
    dimensions: { [dimensionId: number]: IActiveDimension };
    canDeleteEvents: boolean;
    isGQInternalUser?: boolean;
    onDeleteAllClusterArticles?: (
        eventId: number,
        clusterId: string,
        clusterDate: string
    ) => Promise<void>;
    onDeleteAllClusterScores?: (clusterId: string) => Promise<void>;
};

const EventsFeedRow = ({
    index,
    data,
    setSize,
    windowWidth,
    isEventLoaded,
    onEventClick,
    countriesById,
    events,
    isLoading,
    includeMentionCountries,
    onEventDelete,
    dimensions,
    canDeleteEvents,
    isGQInternalUser,
    onDeleteAllClusterArticles,
    onDeleteAllClusterScores,
}: Props) => {
    const rowRef = useRef<HTMLDivElement>();
    useEffect(() => {
        if (!rowRef.current) {
            return;
        }
        setSize(index, rowRef.current.getBoundingClientRect?.().height);
    }, [setSize, index, windowWidth]);
    if (!isEventLoaded(index) && isLoading) {
        return null;
    }
    const event = events[index];
    if (!event) {
        return null;
    }
    const previousEvent = events[index - 1];
    const shouldDrawSeparator =
        previousEvent &&
        index > 0 &&
        event.dateString !== previousEvent.dateString;
    return (
        <div ref={rowRef}>
            {shouldDrawSeparator && (
                <DateSeparator caption={event.dateString} />
            )}
            <EventItem
                bullshit_metric={event.bullshit_metric}
                countries={getEventCountries(
                    event,
                    includeMentionCountries
                )}
                created_at={event.created_at}
                focus_countries={event.focus_countries}
                headline={event.headline}
                id={event.id}
                cluster={event.cluster}
                mentioned_countries={event.mentioned_countries}
                source={event.source}
                time={event.time}
                url={event.url}
                _score={event._score}
                matchFilter={event.matchFilter}
                uscores={event.uscores}
                isActive={event.id === data.activeEventID}
                onClick={onEventClick}
                onDelete={onEventDelete}
                dimensions={dimensions}
                canDeleteEvents={isEventNew(event) && canDeleteEvents}
                countriesById={countriesById}
                isGQInternalUser={isGQInternalUser}
                onDeleteAllClusterArticles={onDeleteAllClusterArticles}
                onDeleteAllClusterScores={onDeleteAllClusterScores}
            />
        </div>
    );
};

export default EventsFeedRow;
